import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import Select, { type MultiValue, type StylesConfig } from "react-select";
import {
  fetchCommunicationLogs,
  type MessageLogItem,
} from "../../services/communicationLogsApi";
import {
  GenericMessagePreview,
  isProbablyHtml,
  stripHtml,
  WhatsAppBubblePreview,
  WhatsAppConversationThreadPreview,
  extractConversationTurns,
} from "../../components/communication/MessagePreviewViews";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";

type LogsSelectOption = { value: string; label: string };

const logsMultiSelectStyles: StylesConfig<LogsSelectOption, true> = {
  control: (base) => ({
    ...base,
    minHeight: 40,
    borderColor: "#d1d5db",
    boxShadow: "none",
    "&:hover": { borderColor: "#9ca3af" },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#fde8e3",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#c2410c",
    fontWeight: 600,
    fontSize: "0.75rem",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#c2410c",
    ":hover": { backgroundColor: "#fecaca", color: "#991b1b" },
  }),
};

const PAGE_SIZE = 20;

const statusClass = (status: string) => {
  const s = status.toLowerCase();
  if (s === "sent" || s === "delivered" || s.includes("sent from")) {
    return "bg-emerald-100 text-emerald-700 border-emerald-200";
  }
  if (s === "failed" || s.includes("fail")) {
    return "bg-rose-100 text-rose-700 border-rose-200";
  }
  if (s === "pending" || s === "queued" || s === "processing") {
    return "bg-amber-100 text-amber-700 border-amber-200";
  }
  return "bg-gray-100 text-gray-700 border-gray-200";
};

/** Resolves MessageQueue log subdocument `timestamp` (ISO string, epoch, Date, or Mongo EJSON `{ $date }`). */
function parseLogEntryTimeMs(raw: unknown): number {
  if (raw == null) return NaN;
  if (typeof raw === "string" || typeof raw === "number") {
    const t = new Date(raw).getTime();
    return Number.isFinite(t) ? t : NaN;
  }
  if (raw instanceof Date) {
    const t = raw.getTime();
    return Number.isFinite(t) ? t : NaN;
  }
  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
    const rec = raw as Record<string, unknown>;
    if ("$date" in rec) {
      return parseLogEntryTimeMs(rec.$date);
    }
    if (typeof rec.$numberLong === "string") {
      const n = Number.parseInt(rec.$numberLong, 10);
      return Number.isFinite(n) ? n : NaN;
    }
  }
  return NaN;
}

type TimelineRow = { message: string; timeIso: string };

/**
 * Timeline from MessageQueue.logs in **collection order** ($push order: queued → sent → … → webhooks).
 * Each line’s clock is **only** `entry.timestamp` (not document `createdAt`).
 */
function buildMessageTimeline(log: MessageLogItem): TimelineRow[] {
  const raw = log.logs;
  const rows: TimelineRow[] = [];

  if (Array.isArray(raw)) {
    for (const entry of raw) {
      if (!entry || typeof entry !== "object") continue;
      const o = entry as Record<string, unknown>;
      const message =
        typeof o.message === "string" && o.message.trim()
          ? o.message.trim()
          : typeof o.eventType === "string" && o.eventType.trim()
            ? o.eventType.trim()
            : "Event";
      const convText = typeof o.text === "string" ? o.text.trim() : "";
      const display =
        convText.length > 0
          ? `${message}: ${convText.length > 100 ? `${convText.slice(0, 100)}…` : convText}`
          : message;
      const ms = parseLogEntryTimeMs(o.timestamp);
      const timeIso = Number.isFinite(ms) ? new Date(ms).toISOString() : "";
      rows.push({ message: display, timeIso });
    }
  }

  if (rows.length === 0) {
    const fallbackMs = parseLogEntryTimeMs(log.updatedAt ?? log.createdAt);
    const timeIso = Number.isFinite(fallbackMs)
      ? new Date(fallbackMs).toISOString()
      : "";
    return [
      {
        message: log.status?.trim() ? String(log.status) : "No events recorded",
        timeIso,
      },
    ];
  }

  return rows;
}

function LogStatusCell({ log }: { log: MessageLogItem }) {
  const entries = useMemo(() => buildMessageTimeline(log), [log]);
  const label = log.status ?? "pending";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex whitespace-nowrap cursor-pointer rounded-full border px-2.5 py-1 text-left text-xs font-semibold uppercase transition hover:ring-2 hover:ring-[#EC6E51]/30 focus:outline-none focus:ring-2 focus:ring-[#EC6E51] ${statusClass(
            label
          )}`}
          aria-label={`Status: ${label}. Open message event timeline.`}
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(100vw-2rem,22rem)] border-gray-200 p-0 shadow-lg data-[state=open]:z-[100]"
        align="start"
        side="bottom"
        sideOffset={6}
      >
        <div className="border-b border-gray-100 px-4 py-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Message timeline
          </p>
          <p className="mt-0.5 font-mono text-[10px] text-gray-400">{log._id}</p>
        </div>
        <ul className="max-h-80 overflow-y-auto px-2 py-3">
          {entries.map((row, i) => (
            <li key={i} className="relative flex gap-0 pb-4 pl-6 last:pb-1">
              <span
                className="absolute left-2 top-1.5 h-2 w-2 shrink-0 rounded-full bg-[#EC6E51] ring-4 ring-white"
                aria-hidden
              />
              {i < entries.length - 1 && (
                <span
                  className="absolute bottom-0 left-[11px] top-5 w-px bg-gray-200"
                  aria-hidden
                />
              )}
              <div className="min-w-0 flex-1 pl-1">
                <p className="text-[11px] font-medium text-gray-500">
                  {row.timeIso ? formatTime(row.timeIso) : "—"}
                </p>
                <p className="mt-0.5 text-sm text-gray-900">{row.message}</p>
              </div>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function formatChannel(channel: string): string {
  const m: Record<string, string> = {
    whatsapp: "WhatsApp",
    sms: "SMS",
    "in-app": "In-App",
    email: "Email",
    call: "Call",
  };
  return m[channel] ?? channel;
}

function formatTime(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/** Summary line + full preview for popover (HTML email bodies vs plain). */
function getEventPreview(log: MessageLogItem): {
  hasPreview: boolean;
  summary: string;
  fullHtml: string | null;
  fullPlain: string;
} {
  const params = log.params;
  if (params?.skipped === true) {
    return {
      hasPreview: true,
      summary: "Skipped row",
      fullHtml: null,
      fullPlain: "Skipped row",
    };
  }
  const body = log.body?.trim();
  if (body && body !== "[skipped]") {
    const asHtml = isProbablyHtml(body);
    const plain = asHtml ? stripHtml(body) : body;
    const summary =
      plain.length > 72 ? `${plain.slice(0, 72)}…` : plain || "(empty)";
    return {
      hasPreview: true,
      summary,
      fullHtml: asHtml ? body : null,
      fullPlain: plain || body,
    };
  }
  if (params?.communicationId != null) {
    const line = `Communication ${String(params.communicationId).slice(0, 10)}…`;
    return {
      hasPreview: true,
      summary: line,
      fullHtml: null,
      fullPlain: `Communication ${String(params.communicationId)}`,
    };
  }
  return { hasPreview: false, summary: "—", fullHtml: null, fullPlain: "" };
}

function LogEventCell({ log, compact = true }: { log: MessageLogItem; compact?: boolean }) {
  const preview = useMemo(() => getEventPreview(log), [log]);
  const conversationTurns = useMemo(() => extractConversationTurns(log), [log]);
  const isWhatsApp = log.channel === "whatsapp";
  const hasConversation = conversationTurns.length > 0;

  if (!preview.hasPreview && !hasConversation) {
    return <span className="text-sm text-gray-400">—</span>;
  }

  const buttonLabel = hasConversation
    ? conversationTurns.length > 1
      ? `View conversation (${conversationTurns.length})`
      : "View conversation"
    : "View message";

  return (
    <div className={`flex min-w-0 items-center ${compact ? "w-full max-w-full" : "w-full max-w-full"}`}>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="min-w-0 max-w-full truncate text-left text-sm font-medium text-[#EC6E51] underline decoration-transparent underline-offset-2 transition hover:decoration-current"
            aria-label="Show message preview"
            title={hasConversation ? "Full WhatsApp conversation" : preview.summary}
          >
            {buttonLabel}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[min(100vw-2rem,20rem)] border-gray-200 p-0 shadow-lg data-[state=open]:z-[100]"
          align="end"
          side="bottom"
          sideOffset={6}
        >
          {isWhatsApp && hasConversation ? (
            <WhatsAppConversationThreadPreview turns={conversationTurns} />
          ) : isWhatsApp ? (
            <WhatsAppBubblePreview
              fullHtml={preview.fullHtml}
              fullPlain={preview.fullPlain}
              sentAt={log.createdAt}
            />
          ) : (
            <GenericMessagePreview fullHtml={preview.fullHtml} fullPlain={preview.fullPlain} />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

function rowRecipient(log: MessageLogItem): string {
  const r = log.recipients;
  if (Array.isArray(r) && r.length > 0) {
    return r.filter(Boolean).join(", ");
  }
  return "—";
}

/** Stacked row for viewports where the wide table is hidden. */
function LogsLogCard({ log }: { log: MessageLogItem }) {
  return (
    <article className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-col gap-3 border-b border-gray-100 pb-3">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Log ID</p>
          <p className="mt-0.5 break-all font-mono text-[11px] leading-snug text-gray-800">
            {log._id}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <LogStatusCell log={log} />
          <span className="text-xs text-gray-500">
            {formatTime(log.createdAt)}
          </span>
        </div>
      </div>
      <dl className="space-y-3 text-sm">
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Event</dt>
          <dd className="mt-1 min-w-0">
            <LogEventCell log={log} compact={false} />
          </dd>
        </div>
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            Recipient
          </dt>
          <dd className="mt-1 break-words text-gray-800">{rowRecipient(log)}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            Channel
          </dt>
          <dd className="mt-1 text-gray-800">{formatChannel(log.channel)}</dd>
        </div>
      </dl>
    </article>
  );
}

const CHANNEL_SELECT_OPTIONS: LogsSelectOption[] = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "sms", label: "SMS" },
  { value: "rcs", label: "RCS" },
  { value: "email", label: "Email" },
  { value: "in-app", label: "In-App" },
];

const STATUS_SELECT_OPTIONS: LogsSelectOption[] = [
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "sent", label: "Sent" },
  { value: "failed", label: "Failed" },
];

const CommunicationLogs: React.FC = () => {
  const navigate = useNavigate();
  /** Empty = all channels. Otherwise OR filter (multi). */
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  /** Empty = all statuses. Otherwise OR filter. */
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [items, setItems] = useState<MessageLogItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCommunicationLogs({
        limit: PAGE_SIZE,
        channels: selectedChannels.length > 0 ? selectedChannels : undefined,
        statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
      });
      if (!data.ok) {
        throw new Error("Unexpected response");
      }
      setItems(data.items);
      setNextCursor(data.nextCursor);
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message?: string }).message)
          : "Failed to load logs";
      setError(msg);
      toast.error(msg);
      setItems([]);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }, [selectedChannels, selectedStatuses]);

  useEffect(() => {
    void loadFirstPage();
  }, [loadFirstPage]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await fetchCommunicationLogs({
        limit: PAGE_SIZE,
        cursor: nextCursor,
        channels: selectedChannels.length > 0 ? selectedChannels : undefined,
        statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
      });
      if (data.ok) {
        setItems((prev) => [...prev, ...data.items]);
        setNextCursor(data.nextCursor);
      }
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message?: string }).message)
          : "Failed to load more";
      toast.error(msg);
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, selectedChannels, selectedStatuses]);

  const channelSelectValue = useMemo(
    () => CHANNEL_SELECT_OPTIONS.filter((o) => selectedChannels.includes(o.value)),
    [selectedChannels]
  );

  const onChannelSelectChange = useCallback((opts: MultiValue<LogsSelectOption>) => {
    setSelectedChannels(opts?.length ? opts.map((o) => o.value) : []);
  }, []);

  const statusSelectValue = useMemo(
    () => STATUS_SELECT_OPTIONS.filter((o) => selectedStatuses.includes(o.value)),
    [selectedStatuses]
  );

  const onStatusSelectChange = useCallback((opts: MultiValue<LogsSelectOption>) => {
    setSelectedStatuses(opts?.length ? opts.map((o) => o.value) : []);
  }, []);

  const rows = useMemo(() => items, [items]);

  return (
    <div className="p-3 sm:p-6">
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">Communication Logs</h1>
          <p className="mt-1 text-sm text-gray-600 sm:text-base">
            Message queue activity from the communication server API.
          </p>
        </div>
        <div className="flex w-full shrink-0 gap-2 sm:w-auto">
          <button
            type="button"
            onClick={() => void loadFirstPage()}
            disabled={loading}
            className="inline-flex w-full items-center justify-center rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            aria-label="Resync logs"
            title="Resync logs"
          >
            <svg
              className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M16.667 10A6.667 6.667 0 1 1 14.714 5.286"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
              <path
                d="M16.667 3.333v3.334h-3.334"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => navigate("/communication-server")}
            className="w-full shrink-0 rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 sm:w-auto"
          >
            Back
          </button>
        </div>
      </div>

      <div className="mb-4 grid gap-4 rounded-lg border border-gray-200 bg-white p-4 md:grid-cols-2">
        <div>
          <div className="mb-2 flex items-start justify-between gap-3">
            <p className="min-w-0 flex-1 text-xs leading-snug text-gray-500">
              Select one or more channels, or leave empty to show all.
            </p>
            <button
              type="button"
              onClick={() => setSelectedChannels([])}
              className="shrink-0 text-xs font-semibold text-[#EC6E51] hover:underline"
            >
              Clear
            </button>
          </div>
          <Select<LogsSelectOption, true>
            isMulti
            closeMenuOnSelect={false}
            options={CHANNEL_SELECT_OPTIONS}
            value={channelSelectValue}
            onChange={onChannelSelectChange}
            placeholder="All channels"
            className="text-sm"
            classNamePrefix="logs-channel-select"
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            styles={logsMultiSelectStyles}
          />
        </div>
        <div>
          <div className="mb-2 flex items-start justify-between gap-3">
            <p className="min-w-0 flex-1 text-xs leading-snug text-gray-500">
              Select one or more statuses, or leave empty to show all.
            </p>
            <button
              type="button"
              onClick={() => setSelectedStatuses([])}
              className="shrink-0 text-xs font-semibold text-[#EC6E51] hover:underline"
            >
              Clear
            </button>
          </div>
          <Select<LogsSelectOption, true>
            isMulti
            closeMenuOnSelect={false}
            options={STATUS_SELECT_OPTIONS}
            value={statusSelectValue}
            onChange={onStatusSelectChange}
            placeholder="All statuses"
            className="text-sm"
            classNamePrefix="logs-status-select"
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            styles={logsMultiSelectStyles}
          />
        </div>
      </div>

      {error && !loading && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
      )}

      {/* Narrow screens: one card per log (avoids crushed 6-column table). */}
      <div className="space-y-3 md:hidden">
        {loading && (
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
            Loading…
          </div>
        )}
        {!loading &&
          rows.map((log) => <LogsLogCard key={log._id} log={log} />)}
        {!loading && rows.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
            No logs found.
          </div>
        )}
      </div>

      <div className="hidden md:block md:overflow-x-auto md:rounded-lg md:border md:border-gray-200 md:bg-white">
        <table className="min-w-[900px] w-full table-fixed divide-y divide-gray-200">
          <colgroup>
            <col className="w-[8.5rem]" />
            <col className="w-[14rem] lg:w-[16rem]" />
            <col className="w-[12rem] lg:w-[14rem]" />
            <col className="w-[7rem]" />
            <col className="w-[10.5rem]" />
            <col className="w-[10rem]" />
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Log ID
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Event
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Recipient
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Channel
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Status
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Time
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((log) => (
                <tr key={log._id} className="hover:bg-gray-50">
                  <td className="px-3 py-3 align-top font-mono text-[10px] leading-snug text-gray-800">
                    <span className="line-clamp-3 break-all" title={log._id}>
                      {log._id}
                    </span>
                  </td>
                  <td className="min-w-0 px-3 py-3 align-top">
                    <LogEventCell log={log} />
                  </td>
                  <td className="min-w-0 px-3 py-3 align-top text-sm text-gray-700">
                    <span className="line-clamp-2 break-all" title={rowRecipient(log)}>
                      {rowRecipient(log)}
                    </span>
                  </td>
                  <td className="px-3 py-3 align-top text-sm text-gray-700">
                    {formatChannel(log.channel)}
                  </td>
                  <td className="px-3 py-3 align-top text-sm">
                    <LogStatusCell log={log} />
                  </td>
                  <td className="whitespace-normal px-3 py-3 align-top text-xs leading-snug text-gray-700">
                    {formatTime(log.createdAt)}
                  </td>
                </tr>
              ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                  No logs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!loading && nextCursor && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
};

export default CommunicationLogs;
